import React, { useEffect, useState } from 'react';
import { http, authCfg } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { HeartHandshake, Loader2, Send, RefreshCw, CheckCircle2, Clock } from 'lucide-react';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-AU') : '—');

export default function WinBackPanel() {
  const { toast } = useToast();
  const [candidates, setCandidates] = useState(null);
  const [history, setHistory] = useState(null);
  const [busyEmail, setBusyEmail] = useState('');
  const [busyAll, setBusyAll] = useState(false);

  const load = () => Promise.all([
    http.get('/admin/winback/candidates', authCfg()).then((r) => setCandidates(r.data)).catch(() => setCandidates([])),
    http.get('/admin/winback/history', authCfg()).then((r) => setHistory(r.data)).catch(() => setHistory({ stats: {}, records: [] })),
  ]);
  useEffect(() => { load(); }, []);

  const sendOne = async (c) => {
    setBusyEmail(c.email);
    try {
      const { data } = await http.post('/admin/winback/send', { email: c.email }, authCfg());
      const s = data.sent?.[0];
      toast({ title: s?.emailStatus === 'sent' ? `Code ${s.code} emailed to ${c.email}` : `Code ${s?.code} created — email ${s?.emailStatus}` });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Could not send', variant: 'destructive' }); }
    finally { setBusyEmail(''); }
  };

  const sendAll = async () => {
    if (!window.confirm(`Email a personal 10% code to all ${candidates.length} gone-quiet VIPs?`)) return;
    setBusyAll(true);
    try {
      const { data } = await http.post('/admin/winback/send', { all: true }, authCfg());
      toast({ title: `Sent ${data.sentCount} win-back code${data.sentCount === 1 ? '' : 's'}` });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Could not send', variant: 'destructive' }); }
    finally { setBusyAll(false); }
  };

  if (!candidates || !history) return <div className="py-16 text-center"><Loader2 className="animate-spin inline text-alp-gold" size={20} /></div>;
  const st = history.stats || {};

  return (
    <div data-testid="winback-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h2 className="font-serif text-2xl text-alp-cream flex items-center gap-2"><HeartHandshake size={20} className="text-alp-gold" /> Win-Back</h2>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-xs tracking-[0.12em] uppercase border border-alp-line px-3 py-1.5 rounded text-alp-cream hover:border-alp-gold hover:text-alp-gold"><RefreshCw size={13} /> Refresh</button>
      </div>
      <p className="text-alp-gray text-sm mb-6">VIPs (2+ orders or $300+ spent) who haven't ordered in 60+ days. Each gets a personal single-use <span className="text-alp-gold">10% code</span> valid 14 days, locked to their email — and we track who comes back.</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="winback-stats">
        {[['Quiet VIPs', candidates.length], ['Codes sent', st.sent || 0], ['Came back', st.returned || 0], ['Revenue recovered', money(st.recovered)]].map(([l, v]) => (
          <div key={l} className="border border-alp-line rounded-lg bg-alp-card/40 p-4">
            <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gray">{l}</p>
            <p className="text-alp-gold font-serif text-xl mt-1">{v}</p>
          </div>
        ))}
      </div>

      <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5 mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold">Ready to send</p>
          {candidates.length > 0 && (
            <button onClick={sendAll} disabled={busyAll} data-testid="winback-send-all"
              className="inline-flex items-center gap-1.5 bg-alp-gold text-alp-black px-4 py-2 rounded text-xs tracking-[0.12em] uppercase font-semibold hover:bg-alp-goldlight disabled:opacity-50">
              {busyAll ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send to all ({candidates.length})
            </button>
          )}
        </div>
        {candidates.length === 0 ? <p className="text-alp-gray text-sm py-4 text-center">No gone-quiet VIPs right now — everyone's either active or already been emailed. 🎉</p> : (
          <div className="border border-alp-line rounded divide-y divide-alp-line/50">
            {candidates.map((c) => (
              <div key={c.email} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`winback-candidate-${c.email}`}>
                <span className="min-w-0 flex-1">
                  <span className="block text-alp-cream text-sm">{c.name || c.email}</span>
                  <span className="block text-alp-gray text-xs">{c.email} · {c.orders} order{c.orders === 1 ? '' : 's'} · {money(c.spend)} lifetime</span>
                </span>
                <span className="text-amber-300 text-xs inline-flex items-center gap-1"><Clock size={12} /> {c.daysQuiet} days quiet</span>
                <button onClick={() => sendOne(c)} disabled={busyEmail === c.email} data-testid={`winback-send-${c.email}`}
                  className="inline-flex items-center gap-1.5 border border-alp-gold/50 text-alp-gold px-3 py-1.5 rounded text-xs tracking-[0.12em] uppercase hover:bg-alp-gold/10 disabled:opacity-50">
                  {busyEmail === c.email ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send 10% code
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5">
        <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">Sent &amp; returned</p>
        {history.records.length === 0 ? <p className="text-alp-gray text-sm py-4 text-center">Nothing sent yet.</p> : (
          <div className="border border-alp-line rounded divide-y divide-alp-line/50 max-h-96 overflow-y-auto" data-testid="winback-history">
            {history.records.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="block text-alp-cream">{r.name || r.email}</span>
                  <span className="block text-alp-gray text-xs">{r.email} · code <span className="text-alp-gold">{r.code}</span> · sent {fmtDate(r.sentAt)} · expires {r.expiry}</span>
                </span>
                {r.returned ? (
                  <span className="inline-flex items-center gap-1.5 text-emerald-300 text-xs" data-testid={`winback-returned-${r.email}`}>
                    <CheckCircle2 size={14} /> Returned · {money(r.recoveredTotal)}{r.usedCode ? ' (used code)' : ''}
                  </span>
                ) : (
                  <span className="text-alp-gray text-xs inline-flex items-center gap-1.5"><Clock size={13} /> Waiting{r.emailStatus !== 'sent' ? ` · email ${r.emailStatus}` : ''}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
